import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, CircleCheck, Clock3, Download, Film as FilmIcon, Languages, LoaderCircle, Search, Sparkles, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ContentTypeSelect } from '../../components/ContentTypeSelect'
import { FilmDetailsDialog } from '../../components/FilmDetailsDialog'
import { SenderLogo } from '../../components/SenderLogo'
import { api, formatDuration, queryString, type ArtworkSelection, type ContentTag, type ContentType, type DiscoveryItem, type DownloadJob, type Facet, type Film, type MediaRoot, type Page } from '../../lib/api'
import { loadLocalPreferences, saveLocalPreferences } from '../../lib/localPreferences'
import { DownloadDialog } from '../catalog/DownloadDialog'

type AvailabilityFilter = 'available' | 'unavailable' | 'all'
const FSK_RATINGS = ['0', '6', '12', '16', '18'] as const
type FskRating = typeof FSK_RATINGS[number]
const DISCOVERY_GENRES = ['CRIME', 'THRILLER', 'COMEDY', 'DRAMA', 'ROMANCE', 'ADVENTURE', 'SCIENCE_FICTION', 'HORROR', 'WAR', 'HISTORY', 'FAMILY'] as const
type DiscoveryGenre = typeof DISCOVERY_GENRES[number]
interface DiscoveryFilterPreferences {
  query: string
  sender: string
  genre: DiscoveryGenre | ''
  maximumFskRating: FskRating | ''
  eventType: DiscoveryItem['eventType'] | ''
  availability: AvailabilityFilter
}
const DISCOVERY_FILTERS_KEY = 'meine-mediathek.discovery-filters.v1'
const DISCOVERY_FILTER_DEFAULTS: DiscoveryFilterPreferences = { query: '', sender: '', genre: '', maximumFskRating: '', eventType: '', availability: 'available' }

export function NewMoviesView({ mediaRoots }: { mediaRoots: MediaRoot[] }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId } = useParams<{ eventId?: string }>()
  const queryClient = useQueryClient()
  const [initialFilters] = useState(() => loadLocalPreferences(DISCOVERY_FILTERS_KEY, DISCOVERY_FILTER_DEFAULTS))
  const [query, setQuery] = useState(initialFilters.query)
  const [sender, setSender] = useState(initialFilters.sender)
  const [genre, setGenre] = useState<DiscoveryGenre | ''>(DISCOVERY_GENRES.includes(initialFilters.genre as DiscoveryGenre) ? initialFilters.genre : '')
  const [maximumFskRating, setMaximumFskRating] = useState<FskRating | ''>(FSK_RATINGS.includes(initialFilters.maximumFskRating as FskRating) ? initialFilters.maximumFskRating : '')
  const [eventType, setEventType] = useState<DiscoveryItem['eventType'] | ''>(initialFilters.eventType)
  const [availability, setAvailability] = useState<AvailabilityFilter>(initialFilters.availability)
  const [downloadFilm, setDownloadFilm] = useState<Film | null>(null)
  useEffect(() => {
    saveLocalPreferences(DISCOVERY_FILTERS_KEY, { query, sender, genre, maximumFskRating, eventType, availability } satisfies DiscoveryFilterPreferences)
  }, [query, sender, genre, maximumFskRating, eventType, availability])
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const senders = useQuery({ queryKey: ['catalog-senders'], queryFn: () => api<Facet[]>('/api/v1/catalog/senders'), staleTime: 5 * 60_000 })
  const downloads = useQuery({ queryKey: ['downloads'], queryFn: () => api<DownloadJob[]>('/api/v1/downloads?limit=1000'), refetchInterval: 15_000 })
  const downloadStates = useMemo(() => {
    const states = new Map<string, DownloadJob['state']>()
    downloads.data?.forEach((job) => {
      if (job.filmId && job.state !== 'DELETED' && job.state !== 'SKIPPED' && !states.has(job.filmId)) states.set(job.filmId, job.state)
    })
    return states
  }, [downloads.data])
  const movies = useInfiniteQuery({
    queryKey: ['new-movies', query, sender, genre, maximumFskRating, eventType, availability],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<Page<DiscoveryItem>>(`/api/v1/discoveries/movies?${queryString({
      query,
      sender,
      genre: genre || undefined,
      maximumFskRating: maximumFskRating || undefined,
      eventType,
      available: availability === 'all' ? undefined : availability === 'available',
      page: pageParam,
      size: 24,
    })}`),
    getNextPageParam: (lastPage) => lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
    refetchInterval: 15_000,
  })
  const discoveries = useMemo(() => movies.data?.pages.flatMap((page) => page.items) ?? [], [movies.data])
  const groupedDiscoveries = useMemo(() => groupByFirstSeenDay(discoveries), [discoveries])
  const filtersActive = Boolean(query || sender || genre || maximumFskRating || eventType || availability !== 'available')
  const listedDiscovery = useMemo(() => discoveries.find((item) => String(item.eventId) === eventId), [eventId, discoveries])
  const detail = useQuery({
    queryKey: ['discoveries', 'detail', eventId],
    queryFn: () => api<DiscoveryItem>(`/api/v1/discoveries/${encodeURIComponent(eventId!)}`),
    enabled: Boolean(eventId && !listedDiscovery),
    initialData: listedDiscovery,
  })
  const selectedDiscovery = eventId ? (listedDiscovery ?? detail.data ?? null) : null
  const selected = selectedDiscovery?.film ?? null
  const classification = useMutation({
    mutationFn: ({ film, contentType, tags }: { film: Film; contentType: ContentType; tags: ContentTag[] }) => api<Film>(
      `/api/v1/catalog/${encodeURIComponent(film.id)}/classification`,
      { method: 'PUT', body: JSON.stringify({ contentType, tags }) },
    ),
    onSuccess: (updated) => {
      queryClient.setQueryData(['catalog', 'detail', updated.id], updated)
      if (eventId) queryClient.setQueryData<DiscoveryItem>(['discoveries', 'detail', eventId], (current) => current ? { ...current, film: updated } : current)
      queryClient.invalidateQueries({ queryKey: ['new-movies'] })
    },
  })
  const artwork = useMutation({
    mutationFn: async (action: { kind: 'url'; selection: ArtworkSelection } | { kind: 'upload'; file: File } | { kind: 'remove' }) => {
      const path = `/api/v1/discoveries/${encodeURIComponent(eventId!)}`
      if (action.kind === 'url') {
        return api<DiscoveryItem>(`${path}/artwork`, { method: 'PUT', body: JSON.stringify(action.selection) })
      }
      if (action.kind === 'upload') {
        const body = new FormData()
        body.append('file', action.file)
        return api<DiscoveryItem>(`${path}/artwork-upload`, { method: 'POST', body })
      }
      return api<DiscoveryItem>(`${path}/artwork`, { method: 'DELETE' })
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['discoveries', 'detail', String(updated.eventId)], updated)
      queryClient.setQueriesData<{ pages: Page<DiscoveryItem>[]; pageParams: unknown[] }>({ queryKey: ['new-movies'] }, (current) => current ? {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => item.eventId === updated.eventId ? updated : item),
        })),
      } : current)
    },
  })
  const metadataRetry = useMutation({
    mutationFn: () => api<DiscoveryItem>(
      `/api/v1/discoveries/${encodeURIComponent(eventId!)}/metadata-retry`, { method: 'POST' }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['discoveries', 'detail', String(updated.eventId)], updated)
      queryClient.setQueriesData<{ pages: Page<DiscoveryItem>[]; pageParams: unknown[] }>({ queryKey: ['new-movies'] }, (current) => current ? {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => item.eventId === updated.eventId ? updated : item),
        })),
      } : current)
      queryClient.invalidateQueries({ queryKey: ['metadata-status'] })
    },
  })

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !movies.hasNextPage) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !movies.isFetchingNextPage) movies.fetchNextPage()
    }, { rootMargin: '500px 0px' })
    observer.observe(target)
    return () => observer.disconnect()
  }, [movies.fetchNextPage, movies.hasNextPage, movies.isFetchingNextPage])

  const openDetails = (item: DiscoveryItem) => navigate(`/neu/${item.eventId}`, { state: { discoveryOverlay: true } })
  const closeDetails = () => {
    const state = location.state as { discoveryOverlay?: boolean } | null
    if (state?.discoveryOverlay) navigate(-1)
    else navigate('/neu', { replace: true })
  }
  const clearFilters = () => {
    setQuery('')
    setSender('')
    setGenre('')
    setMaximumFskRating('')
    setEventType('')
    setAvailability('available')
  }

  return <section className="page discovery-page">
    <div className="discovery-intro panel">
      <div><span className="eyebrow"><Sparkles />{t('newMovies.eyebrow')}</span><h1>{t('newMovies.title')}</h1><p>{t('newMovies.subtitle')}</p></div>
      <div className="discovery-filters">
        <DiscoverySearchInput value={query} onChange={setQuery} placeholder={t('newMovies.searchPlaceholder')} />
        <label className="compact-select"><span>{t('sendungen.sender')}</span><select value={sender} onChange={(event) => setSender(event.target.value)}><option value="">{t('common.all')}</option>{senders.data?.map((facet) => <option value={facet.value} key={facet.value}>{facet.value}</option>)}</select></label>
        <label className="compact-select"><span>{t('newMovies.genre')}</span><select value={genre} onChange={(event) => setGenre(event.target.value as DiscoveryGenre | '')}><option value="">{t('newMovies.allGenres')}</option>{DISCOVERY_GENRES.map((value) => <option value={value} key={value}>{t(`newMovies.genres.${value}`)}</option>)}</select></label>
        <label className="compact-select" title={t('newMovies.fskFilterHint')}><span>{t('newMovies.fsk')}</span><select value={maximumFskRating} onChange={(event) => setMaximumFskRating(event.target.value as FskRating | '')}><option value="">{t('newMovies.allFskRatings')}</option>{FSK_RATINGS.map((value) => <option value={value} key={value}>{t('newMovies.maximumFsk', { rating: value })}</option>)}</select></label>
        <label className="compact-select"><span>{t('newMovies.eventType')}</span><select value={eventType} onChange={(event) => { const value = event.target.value as DiscoveryItem['eventType'] | ''; setEventType(value); if (value === 'REMOVED') setAvailability('unavailable') }}><option value="">{t('newMovies.allEventTypes')}</option><option value="ADDED">{t('newMovies.newBadge')}</option><option value="REAPPEARED">{t('newMovies.availableAgain')}</option><option value="REMOVED">{t('newMovies.removedBadge')}</option><option value="BASELINE">{t('newMovies.baselineBadge')}</option></select></label>
        <label className="compact-select"><span>{t('newMovies.availability')}</span><select value={availability} onChange={(event) => setAvailability(event.target.value as AvailabilityFilter)}><option value="available">{t('newMovies.availableNow')}</option><option value="all">{t('newMovies.allAvailability')}</option><option value="unavailable">{t('newMovies.unavailableNow')}</option></select></label>
        {filtersActive && <button className="quiet-button discovery-clear-filters" type="button" onClick={clearFilters}><X />{t('newMovies.clearFilters')}</button>}
      </div>
    </div>

    {movies.isLoading && <div className="panel discovery-state">{t('common.loading')}</div>}
    {movies.isError && <div className="panel discovery-state error">{movies.error.message}</div>}
    {movies.data && discoveries.length === 0 && <div className="panel discovery-state"><FilmIcon /><strong>{t(filtersActive ? 'newMovies.noFilterResults' : 'newMovies.empty')}</strong><p>{t(filtersActive ? 'newMovies.noFilterResultsHint' : 'newMovies.emptyHint')}</p>{filtersActive ? <button className="quiet-button" type="button" onClick={clearFilters}>{t('newMovies.clearFilters')}</button> : <Link className="quiet-button" to="/sendungen">{t('newMovies.showAllMovies')}</Link>}</div>}
    {movies.data && discoveries.length > 0 && <>
      <div className="discovery-summary"><strong>{t('newMovies.resultCount', { count: movies.data.pages[0]?.totalItems ?? 0 })}</strong><span>{t('newMovies.precisionHint')}</span></div>
      <div className="discovery-timeline">
        {groupedDiscoveries.map(([day, items]) => <section className="discovery-day" key={day}>
          <header><span /><h2>{dayLabel(day, t)}</h2><b>{items.length}</b></header>
          <div className="movie-discovery-grid">
            {items.map((item) => <MovieDiscoveryCard
              item={item}
              downloadState={discoveryDownloadState(item, downloadStates.get(item.film.id))}
              classificationUpdating={classification.isPending}
              key={item.eventId}
              onOpen={openDetails}
              onOpenDownloads={() => navigate('/downloads')}
              onClassify={(film, contentType, tags) => classification.mutate({ film, contentType, tags })}
              onDownload={setDownloadFilm}
            />)}
          </div>
        </section>)}
      </div>
      <div className="discovery-load-more" ref={loadMoreRef}>{movies.isFetchingNextPage ? t('newMovies.loadingMore') : movies.hasNextPage ? t('newMovies.moreOnScroll') : t('newMovies.end')}</div>
    </>}

    {selected && <FilmDetailsDialog film={selected} metadata={selectedDiscovery?.metadata} metadataEnrichment={selectedDiscovery?.metadataEnrichment} metadataRetrying={metadataRetry.isPending} metadataRetryError={metadataRetry.isError} onMetadataRetry={() => metadataRetry.mutate()} libraryStatus={selectedDiscovery?.library} artworkEventId={selectedDiscovery?.eventId} artworkUrl={selectedDiscovery?.metadata?.artworkAvailable ? discoveryArtworkUrl(selectedDiscovery) : undefined} downloadDisabled={selectedDiscovery?.available === false} classificationUpdating={classification.isPending} classificationError={classification.isError} onClassificationChange={(contentType, tags) => classification.mutate({ film: selected, contentType, tags })} onArtworkUrl={(selection) => artwork.mutateAsync({ kind: 'url', selection }).then(() => undefined)} onArtworkUpload={(file) => artwork.mutateAsync({ kind: 'upload', file }).then(() => undefined)} onArtworkRemove={() => artwork.mutateAsync({ kind: 'remove' }).then(() => undefined)} onClose={closeDetails} onDownload={() => { const film = selected; closeDetails(); setDownloadFilm(film) }} />}
    {downloadFilm && <DownloadDialog film={downloadFilm} mediaRoots={mediaRoots} onClose={() => setDownloadFilm(null)} />}
  </section>
}

function discoveryArtworkUrl(item: DiscoveryItem) {
  const revision = item.metadata?.artworkUpdatedAt
  return `/api/v1/discoveries/${item.eventId}/artwork${revision ? `?v=${encodeURIComponent(revision)}` : ''}`
}

function MovieDiscoveryCard({ item, downloadState, classificationUpdating, onOpen, onOpenDownloads, onClassify, onDownload }: {
  item: DiscoveryItem
  downloadState?: DownloadJob['state']
  classificationUpdating: boolean
  onOpen: (item: DiscoveryItem) => void
  onOpenDownloads: () => void
  onClassify: (film: Film, contentType: ContentType, tags: ContentTag[]) => void
  onDownload: (film: Film) => void
}) {
  const { t } = useTranslation()
  const [format, setFormat] = useState<'unknown' | 'landscape' | 'poster' | 'square'>('unknown')
  const film = item.film
  const imageUrl = item.metadata?.artworkAvailable ? discoveryArtworkUrl(item) : null
  const metadataState = item.metadataEnrichment.status === 'MATCHED'
    ? null
    : item.metadataEnrichment.status
  const alwaysShowSummary = !imageUrl || format === 'landscape' || format === 'square'
  const overview = item.metadata?.overview || film.description || t('newMovies.noDescription')
  const genres = item.metadata?.genres.slice(0, 3) ?? []
  const libraryProfiles = item.library.copies.map((copy) => copy.downloadProfileName || copy.mediaRootLabel)
    .filter((value, index, values) => values.indexOf(value) === index)
  useEffect(() => setFormat('unknown'), [imageUrl])
  const open = () => onOpen(item)
  return <article
    className={`movie-discovery-card artwork-${format} ${alwaysShowSummary ? 'summary-always' : ''} ${item.available ? '' : 'unavailable'}`}
    tabIndex={0}
    aria-label={t('newMovies.openDetails', { title: film.title })}
    onClick={open}
    onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); open() } }}
  >
    <div className="movie-card-art" aria-hidden="true">
      <FilmIcon className="movie-card-placeholder" />
      {imageUrl && <>
        <img className="movie-card-art-backdrop" key={`${imageUrl}-backdrop`} src={imageUrl} alt="" loading="lazy" />
        <img className="movie-card-art-image" key={imageUrl} src={imageUrl} alt="" loading="lazy"
          onLoad={(event) => setFormat(imageFormat(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))}
          onError={(event) => { event.currentTarget.hidden = true; setFormat('landscape') }} />
      </>}
    </div>
    <div className="movie-card-topline">
      <div className="movie-card-sender-corner">
        <SenderLogo sender={film.sender} compact />
        {item.metadata?.fskRating != null && <span className="movie-card-fsk-pennant" data-rating={item.metadata.fskRating}>FSK {item.metadata.fskRating}</span>}
      </div>
      <div className="movie-card-badges"><span className="movie-card-event">{eventBadge(item.eventType, t)}</span>{genres.length > 0 && <span className="movie-card-genre-badge">{genres[0]}</span>}{metadataState && <span className={`movie-card-metadata-badge status-${metadataState.toLowerCase()}`}>{t(`newMovies.metadataStates.${metadataState}`)}</span>}{film.audioVariants.length > 0 && <span className="movie-card-language-badge" title={t('sendungen.includesAudioLanguage', { language: film.audioVariants.map((variant) => variant.label).join(', ') })}><Languages />+ {film.audioVariants.map((variant) => variant.label).join(', ')}</span>}{item.library.downloaded && <span className="movie-card-library-badge" title={t('newMovies.libraryProfiles', { profiles: libraryProfiles.join(', ') })}><CircleCheck />{t('newMovies.libraryBadge')}</span>}</div>
    </div>
    <div className="movie-card-overlay">
      <small>{film.topic}</small>
      <h2>{film.title}</h2>
      {genres.length > 0 && <div className="movie-card-genres">{genres.map((genre) => <span key={genre}>{genre}</span>)}</div>}
      {item.library.downloaded && <div className="movie-card-library-summary"><CircleCheck />{t('newMovies.librarySummary', { profiles: libraryProfiles.join(', ') })}</div>}
      <p>{overview}</p>
      <div className="movie-card-meta"><span><CalendarDays />{film.broadcastAt ? new Date(film.broadcastAt).toLocaleDateString('de-DE') : '—'}</span><span><Clock3 />{formatDuration(film.durationSeconds)}{item.metadata?.releaseYear ? ` · ${item.metadata.releaseYear}` : ''}</span></div>
      <time dateTime={item.occurredAt}>{eventTime(item, t)}</time>
    </div>
    <div className="movie-card-actions">
      <ContentTypeSelect film={film} compact disabled={classificationUpdating} onChange={(contentType, tags) => onClassify(film, contentType, tags)} />
      <MovieDownloadButton item={item} state={downloadState} onDownload={() => onDownload(film)} onOpenDownloads={onOpenDownloads} />
    </div>
  </article>
}

function MovieDownloadButton({ item, state, onDownload, onOpenDownloads }: {
  item: DiscoveryItem
  state?: DownloadJob['state']
  onDownload: () => void
  onOpenDownloads: () => void
}) {
  const { t } = useTranslation()
  const active = state === 'QUEUED' || state === 'RUNNING'
  const visibleState = active || state === 'COMPLETED' ? state : undefined
  const profiles = item.library.copies.map((copy) => copy.downloadProfileName || copy.mediaRootLabel)
    .filter((value, index, values) => values.indexOf(value) === index)
  const title = !item.available
    ? t('newMovies.unavailable')
    : active
      ? `${t(`downloads.states.${state}`)} · ${t('nav.downloads')}`
      : visibleState === 'COMPLETED' && profiles.length > 0
        ? t('sendungen.downloadedAction', { profiles: profiles.join(', ') })
        : t('sendungen.download')
  const icon = visibleState === 'QUEUED'
    ? <Clock3 />
    : visibleState === 'RUNNING'
      ? <LoaderCircle className="spin" />
      : visibleState === 'COMPLETED'
        ? <CircleCheck />
        : <Download />
  return <button
    className={`movie-card-download-button ${visibleState ? `state-${visibleState.toLowerCase()}` : ''}`}
    type="button"
    disabled={!item.available}
    title={title}
    aria-label={`${title}: ${item.film.title}`}
    onClick={(event) => {
      event.stopPropagation()
      if (!item.available) return
      if (active) onOpenDownloads()
      else onDownload()
    }}
  >{icon}{visibleState && <span>{t(`sendungen.downloadState.${visibleState}`)}</span>}</button>
}

function discoveryDownloadState(item: DiscoveryItem, recordedState?: DownloadJob['state']): DownloadJob['state'] | undefined {
  if (recordedState === 'QUEUED' || recordedState === 'RUNNING') return recordedState
  if (item.library.downloaded || recordedState === 'COMPLETED') return 'COMPLETED'
  return recordedState
}

function imageFormat(width: number, height: number): 'landscape' | 'poster' | 'square' {
  if (width <= 0 || height <= 0) return 'landscape'
  const ratio = width / height
  if (ratio < .86) return 'poster'
  if (ratio < 1.18) return 'square'
  return 'landscape'
}

function DiscoverySearchInput({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  const [input, setInput] = useState(value)
  useEffect(() => setInput(value), [value])
  useEffect(() => {
    const timer = window.setTimeout(() => onChange(input.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [input, onChange])
  return <div className="search-box discovery-search"><Search /><input value={input} onChange={(event) => setInput(event.target.value)} placeholder={placeholder} aria-label={placeholder} />{input && <button type="button" aria-label="Suche leeren" onClick={() => setInput('')}><X /></button>}</div>
}

function groupByFirstSeenDay(items: DiscoveryItem[]) {
  const groups = new Map<string, DiscoveryItem[]>()
  items.forEach((item) => {
    const date = new Date(item.occurredAt)
    const key = item.eventType === 'BASELINE' ? 'baseline' : Number.isNaN(date.getTime()) ? 'unknown' : [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
    groups.set(key, [...(groups.get(key) ?? []), item])
  })
  return [...groups.entries()]
}

function dayLabel(value: string, t: (key: string, options?: Record<string, unknown>) => string) {
  if (value === 'baseline') return t('newMovies.baselineGroup')
  if (value === 'unknown') return t('newMovies.unknownDay')
  const date = new Date(`${value}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const difference = Math.round((today.getTime() - date.getTime()) / 86_400_000)
  if (difference === 0) return t('newMovies.today')
  if (difference === 1) return t('newMovies.yesterday')
  return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
}

function eventBadge(type: DiscoveryItem['eventType'], t: (key: string) => string) {
  if (type === 'BASELINE') return t('newMovies.baselineBadge')
  if (type === 'REAPPEARED') return t('newMovies.availableAgain')
  if (type === 'REMOVED') return t('newMovies.removedBadge')
  return t('newMovies.newBadge')
}

function eventTime(item: DiscoveryItem, t: (key: string, options?: Record<string, unknown>) => string) {
  if (item.eventType === 'BASELINE') return t('newMovies.baselineTime')
  const time = new Date(item.occurredAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  if (item.eventType === 'REMOVED') return t('newMovies.removedAt', { time })
  return item.eventType === 'REAPPEARED'
    ? t('newMovies.reappearedAt', { time })
    : t('newMovies.firstSeenAt', { time })
}
